/**
 * ReleaseNotesContainer Component
 *
 * This container component handles all business logic for the Release Notes feature.
 * It manages state, API calls, and passes data and handlers to the view component.
 *
 * Features:
 * - Fetching, creating, updating, and deleting release notes
 * - Form state management for creating/editing notes
 * - Pagination handling
 * - Error handling and loading states
 * - Confirmation modal for deletions
 */
/**
 * ReleaseNotesContainer Component
 *
 * Container component for the Release Notes feature.
 * Handles state, API calls, and business logic.
 */
import React, { useEffect, useState } from "react";
import moment from "moment";
import ReleaseNotesView from "./ReleaseNotesView";
import {
  getReleaseNotes,
  createReleaseNote,
  updateReleaseNote,
  deleteReleaseNote
} from "../services/releaseNotes";

/**
 * Container component that manages the state and logic for Release Notes.
 * Handles CRUD operations and pagination for release notes.
 * @returns {JSX.Element} ReleaseNotesView component with props
 */
const ReleaseNotesContainer = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    title: "",
    note_text: "",
    publish: false,
    published_at: moment() // Set today's date as default
  });
  const [editingId, setEditingId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    perpage: 10,
    total: 0
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /**
   * Fetches release notes from the API with pagination
   * @param {number} page - Page number to fetch
   * @param {number} perpage - Number of items per page
   * @returns {Promise<void>} - Promise that resolves when notes are fetched
   */
  const fetchNotes = async (page = 1, perpage = 10) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch release notes from the API with pagination
      const res = await getReleaseNotes(page, perpage);
      setNotes(res.data.data.release_notes || []);
      setPagination({
        page,
        perpage,
        total: res.data.data.total_records || 0
      });
    } catch (err) {
      // Display user-friendly error message
      setError("Failed to fetch release notes");
    }
    setLoading(false);
  };

  useEffect(() => {
    // Fetch release notes when component mounts
    fetchNotes(pagination.page, pagination.perpage);
    // eslint-disable-next-line
  }, []);

  /**
   * Updates the form state when input fields change
   * @param {string} field - Name of the field to update
   * @param {any} value - New value for the field
   */
  const handleFormChange = (field, value) => {
    // Update form state when input fields change
    setForm(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Handles form submission for creating or updating release notes
   * Determines whether to create a new note or update an existing one
   * based on the editingId state
   * @returns {Promise<void>} - Promise that resolves when the operation completes
   */
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare form data - only include published_at if publish is true
      const formData = { ...form };
      if (!formData.publish) {
        delete formData.published_at;
      }

      if (editingId) {
        // Update existing release note
        await updateReleaseNote(editingId, formData);
      } else {
        // Create new release note
        await createReleaseNote(formData);
      }
      setForm({
        title: "",
        note_text: "",
        publish: false,
        published_at: moment()
      });
      setEditingId(null);
      fetchNotes(pagination.page, pagination.perpage);
    } catch (err) {
      // Show error when saving fails
      setError("Failed to save release note");
    }
    setLoading(false);
  };

  /**
   * Handles editing a release note
   * Populates the form with the selected note data and sets the editingId
   * @param {Object} note - The release note object to edit
   */
  const handleEdit = note => {
    // Populate form with the selected note data for editing
    setForm({
      title: note.title,
      note_text: note.note_text,
      publish: note.publish,
      published_at: note.published_at ? moment(note.published_at) : moment()
    });
    setEditingId(note.id);
  };

  /**
   * Handles the deletion of a release note
   * Called when the user confirms deletion in the modal
   * @returns {Promise<void>} - Promise that resolves when deletion completes
   */
  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      // Delete the release note with the stored ID
      await deleteReleaseNote(deleteId);
      setModalVisible(false);
      setDeleteId(null);
      fetchNotes(pagination.page, pagination.perpage);
    } catch (err) {
      // Show error when deletion fails
      setError("Failed to delete release note");
    }
    setLoading(false);
  };

  /**
   * Opens the delete confirmation modal and stores the ID of the note to delete
   * @param {string|number} id - ID of the release note to delete
   */
  const openDeleteModal = id => {
    // Store the ID of the note to be deleted and show confirmation modal
    setDeleteId(id);
    setModalVisible(true);
  };

  /**
   * Closes the delete confirmation modal and clears the stored delete ID
   */
  const closeDeleteModal = () => {
    // Close the confirmation modal and clear the delete ID
    setModalVisible(false);
    setDeleteId(null);
  };

  /**
   * Handles pagination changes
   * @param {number} page - New page number
   * @param {number} perpage - Items per page
   */
  const handlePageChange = (page, perpage) => {
    // Fetch a new page of release notes when pagination changes
    fetchNotes(page, perpage);
  };

  /**
   * Cancels the current edit operation
   * Resets the form and clears the editingId
   */
  const handleCancelEdit = () => {
    // Reset the form and exit edit mode
    setForm({
      title: "",
      note_text: "",
      publish: false,
      published_at: moment()
    });
    setEditingId(null);
  };

  // Render the view component with all required props
  return (
    <ReleaseNotesView
      notes={notes}
      loading={loading}
      error={error}
      form={form}
      onFormChange={handleFormChange}
      onSubmit={handleSubmit}
      onEdit={handleEdit}
      onDelete={openDeleteModal}
      editingId={editingId}
      onCancelEdit={handleCancelEdit}
      pagination={pagination}
      onPageChange={handlePageChange}
      modalVisible={modalVisible}
      onModalOk={handleDelete}
      onModalCancel={closeDeleteModal}
    />
  );
};

export default ReleaseNotesContainer;
